import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3,
  CalendarDays,
  ClipboardCopy,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Plus,
  Search,
  Users,
  X,
} from "lucide-react";
import {
  HOUSING_DEVELOPMENT_SOURCE_VALUES,
  NOVEMBER_CAREERS_SURVEY,
  NOVEMBER_CAREERS_SURVEY_ID,
  NOVEMBER_HOUSING_DEVELOPMENT_EVENT,
  SURVEY_QUESTION_TYPES,
  buildHousingSurveyAbsoluteUrl,
} from "../config";
import HousingDevelopmentQr from "../HousingDevelopmentQr";
import EventFlyer from "../../components/EventFlyer";
import { validateEventFlyer } from "../../eventMedia";
import AccessibleBarChart from "./AccessibleBarChart";
import {
  buildHousingSurveyReport,
  filterHousingResponses,
  formatAdminDate,
  getHousingDevelopmentMetrics,
  getPartnerReport,
  getPublishedSurveysForEvent,
  listPartnerNames,
} from "./housingDevelopmentReporting";
import {
  downloadHousingSurveyCsv,
  housingSurveyCsvFilename,
  housingSurveyResponsesToCsv,
  selectHousingSurveyForCsv,
} from "./housingDevelopmentCsv";
import {
  createHousingDevelopmentEvent,
  createHousingDevelopmentSurvey,
  duplicateHousingDevelopmentSurvey,
  isHousingSurveySlugLocked,
  replaceHousingDevelopmentEventFlyer,
  setHousingSurveyStatus,
  subscribeHousingDevelopmentEvents,
  subscribeHousingDevelopmentResponses,
  subscribeHousingDevelopmentSurveys,
  updateHousingDevelopmentEvent,
  updateHousingDevelopmentSurvey,
  validateHousingEvent,
  validateHousingSurvey,
} from "./housingDevelopmentAdminService";
import "./HousingDevelopmentAdmin.css";

const TABS = ["Overview", "Events", "Surveys", "Responses", "Partners", "Reports"];

const EMPTY_EVENT = {
  title: "",
  date: "",
  startTime: "09:00",
  endTime: "14:00",
  venueName: "Phoenix Heights Community Centre",
  address: "140a Byng Street, Isle of Dogs, London, E14 9AR",
  description: "",
  partnerOrganisations: "Urban Unity Network",
  registrationEnabled: false,
  registrationUrl: "",
  registrationCount: "",
  attendanceCount: "",
  surveyIds: [],
  status: "draft",
};

const EMPTY_SURVEY = {
  surveyName: "",
  surveySlug: "",
  description: "",
  eventId: "",
  partnerOrganisations: "Urban Unity Network",
  status: "draft",
  publicVisibility: true,
  openDate: "",
  closeDate: "",
  questions: [],
};

const EMPTY_FILTERS = {
  search: "",
  eventId: "",
  surveyId: "",
  partner: "",
  source: "",
  responseCategory: "",
  dateFrom: "",
  dateTo: "",
};

function clean(value) {
  return String(value ?? "").normalize("NFKC").trim();
}

function recordId(record, kind) {
  return clean(record?.[`${kind}Id`] || record?.id);
}

function recordPartners(record) {
  const raw = record?.partnerOrganisations || record?.partners || [];
  return (Array.isArray(raw) ? raw : [raw])
    .map((item) => clean(typeof item === "object" ? item.name || item.label : item))
    .filter(Boolean);
}

function numericMetricValue(record, fields) {
  for (const field of fields) {
    const raw = record?.[field];
    if (raw !== null && raw !== undefined && raw !== "") {
      const value = Number(raw);
      if (Number.isFinite(value)) return value;
    }
  }
  return null;
}

function mergeSeed(seed, stored, kind) {
  const records = new Map(
    process.env.NODE_ENV !== "production"
      ? [[recordId(seed, kind), { ...seed }]]
      : [],
  );
  stored.forEach((item) => {
    const id = recordId(item, kind);
    records.set(id, { ...(records.get(id) || {}), ...item });
  });
  return [...records.values()].filter((item) => recordId(item, kind));
}

function questionTypeChoices() {
  const raw = Array.isArray(SURVEY_QUESTION_TYPES)
    ? SURVEY_QUESTION_TYPES
    : Object.values(SURVEY_QUESTION_TYPES || {});
  return raw.map((item) => typeof item === "object"
    ? { value: item.value || item.id || item.type, label: item.label || item.name || item.value }
    : { value: item, label: item });
}

function typeKey(value) {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function needsOptions(value) {
  return ["singlechoice", "multiplechoice", "dropdown", "demographic"].includes(typeKey(value));
}

function isCustomRating(value) {
  return typeKey(value) === "ratingscale";
}

function isPersonalQuestionType(value) {
  return ["email", "phone"].includes(typeKey(value));
}

function newQuestion(index) {
  return {
    id: `question-${index + 1}`,
    label: "",
    type: questionTypeChoices()[0]?.value || "shortText",
    required: false,
    helpText: "",
    options: [],
    personalData: false,
    ratingMin: 1,
    ratingMax: 5,
  };
}

function surveyToForm(survey) {
  return {
    surveyName: survey.surveyName || survey.title || "",
    surveySlug: survey.surveySlug || survey.slug || "",
    description: survey.description || "",
    eventId: survey.eventId || survey.eventIds?.[0] || "",
    partnerOrganisations: recordPartners(survey).join(", "),
    status: survey.status || (survey.published ? "published" : "draft"),
    publicVisibility: survey.publicVisibility !== false,
    openDate: survey.openDate || "",
    closeDate: survey.closeDate || "",
    questions: (survey.questions || []).map((question) => ({
      ...question,
      helpText: question.helpText || question.helper || "",
      options: [...(question.options || [])],
    })),
  };
}

function eventToForm(event) {
  return {
    title: event.title || event.eventName || "",
    date: event.date || event.startDate || "",
    startTime: event.startTime || "09:00",
    endTime: event.endTime || "14:00",
    venueName: event.venueName || event.location || "",
    address: event.address || [event.addressLine1, event.area, event.city, event.postcode].filter(Boolean).join(", "),
    description: event.description || "",
    partnerOrganisations: recordPartners(event).join(", "),
    registrationEnabled: event.registrationEnabled === true || event.registrationOpen === true,
    registrationUrl: event.registrationUrl || "",
    registrationCount: event.registrationCount ?? event.registrations ?? "",
    attendanceCount: event.attendanceCount ?? event.attended ?? "",
    surveyIds: Array.isArray(event.surveyIds)
      ? [...event.surveyIds]
      : event.surveyId
        ? [event.surveyId]
        : [],
    status: event.status === "completed" ? "approved" : event.status || "draft",
  };
}

function MetricCard({ label, value, icon: Icon = BarChart3 }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <Icon className="h-6 w-6 text-cyan-300" aria-hidden="true" />
      <strong className="mt-3 block text-3xl">{value}</strong>
      <span className="text-white/65">{label}</span>
    </div>
  );
}

function Field({ label, name, error, children, className = "" }) {
  const errorId = `housing-admin-${name}-error`;
  return (
    <label className={`grid gap-2 ${className}`}>
      <span className="font-bold">{label}</span>
      {children}
      {error ? <span id={errorId} className="text-sm text-amber-200">{error}</span> : null}
    </label>
  );
}

function inputClass() {
  return "min-h-12 rounded-xl border border-white/15 bg-slate-950 px-3 text-white";
}

function Status({ children }) {
  return <span className="hd-admin-status">{children || "draft"}</span>;
}

function SurveyPreview({ survey, onClose }) {
  if (!survey) return null;
  const questions = Array.isArray(survey.questions) ? survey.questions : [];
  const previewField = (question, index) => {
    const type = typeKey(question.type);
    const options = question.options || (type === "yesno" ? ["Yes", "No"] : []);
    if (["singlechoice", "yesno", "demographic"].includes(type)) {
      return <div className="grid gap-2">{options.map((option) => <label key={option} className="flex gap-2"><input type="radio" disabled name={`preview-${index}`} /> {option}</label>)}</div>;
    }
    if (type === "multiplechoice") {
      return <div className="grid gap-2">{options.map((option) => <label key={option} className="flex gap-2"><input type="checkbox" disabled /> {option}</label>)}</div>;
    }
    if (type === "dropdown") {
      return <select className={inputClass()} disabled><option>Select an option</option>{options.map((option) => <option key={option}>{option}</option>)}</select>;
    }
    if (["ratingscale", "rating1to5", "rating15", "rating1to10", "rating110"].includes(type)) {
      const min = Number(question.ratingMin ?? question.min ?? 1);
      const max = type.includes("10") ? 10 : Number(question.ratingMax ?? question.max ?? 5);
      return <div className="flex flex-wrap gap-2">{Array.from({ length: Math.max(0, max - min + 1) }, (_, offset) => min + offset).map((value) => <span key={value} className="rounded-lg border border-white/15 px-3 py-2">{value}</span>)}</div>;
    }
    if (type === "longtext") return <textarea className={`${inputClass()} p-3`} rows="4" readOnly />;
    return <input className={inputClass()} type={type === "email" ? "email" : type === "phone" ? "tel" : type === "date" ? "date" : "text"} readOnly />;
  };
  return (
    <div className="hd-admin-preview-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="hd-admin-preview-panel" role="dialog" aria-modal="true" aria-labelledby="housing-survey-preview-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-bold uppercase tracking-widest text-cyan-300">Administrator preview</p><h2 id="housing-survey-preview-title" className="mt-2 text-2xl font-black">{survey.surveyName || survey.title}</h2></div><button type="button" className="rounded-xl border border-white/20 px-3 py-2 font-bold" onClick={onClose}><X className="inline h-4 w-4" /> Close</button></div>
        <p className="mt-3 text-white/70">{survey.description || survey.introduction}</p>
        <div className="mt-6 grid gap-5">{questions.map((question, index) => <div key={question.id || index} className="rounded-xl border border-white/10 bg-black/20 p-4"><p className="font-bold">{index + 1}. {question.label || question.question}{question.required ? " *" : ""}</p>{question.helpText || question.helper ? <p className="mt-1 text-sm text-white/55">{question.helpText || question.helper}</p> : null}<div className="mt-3">{previewField(question, index)}</div></div>)}</div>
      </section>
    </div>
  );
}

export default function HousingDevelopmentAdmin() {
  const [tab, setTab] = useState("Overview");
  const [storedEvents, setStoredEvents] = useState([]);
  const [storedSurveys, setStoredSurveys] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [eventErrors, setEventErrors] = useState({});
  const [eventFlyer, setEventFlyer] = useState(null);
  const [replacementFlyers, setReplacementFlyers] = useState({});
  const [replacingFlyerId, setReplacingFlyerId] = useState("");
  const [savingEvent, setSavingEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState("");
  const [surveyForm, setSurveyForm] = useState(EMPTY_SURVEY);
  const [surveyErrors, setSurveyErrors] = useState({});
  const [editingSurveyId, setEditingSurveyId] = useState("");
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [busySurveyId, setBusySurveyId] = useState("");
  const [qrSurveyId, setQrSurveyId] = useState(NOVEMBER_CAREERS_SURVEY_ID);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [reportFilters, setReportFilters] = useState({
    ...EMPTY_FILTERS,
    surveyId: NOVEMBER_CAREERS_SURVEY_ID,
  });
  const [includePersonalData, setIncludePersonalData] = useState(false);
  const [freeTextSearch, setFreeTextSearch] = useState("");
  const [previewSurvey, setPreviewSurvey] = useState(null);

  useEffect(() => {
    let readyCount = 0;
    const markReady = () => {
      readyCount += 1;
      if (readyCount === 3) setLoading(false);
    };
    const fail = () => {
      setError("Housing & Development admin data could not be loaded. Check your administrator access and connection.");
      setLoading(false);
    };
    const unsubscribeEvents = subscribeHousingDevelopmentEvents((items) => { setStoredEvents(items); markReady(); }, fail);
    const unsubscribeSurveys = subscribeHousingDevelopmentSurveys((items) => { setStoredSurveys(items); markReady(); }, fail);
    const unsubscribeResponses = subscribeHousingDevelopmentResponses((items) => { setResponses(items); markReady(); }, fail);
    return () => {
      unsubscribeEvents();
      unsubscribeSurveys();
      unsubscribeResponses();
    };
  }, []);

  const events = useMemo(
    () => mergeSeed(NOVEMBER_HOUSING_DEVELOPMENT_EVENT, storedEvents, "event")
      .sort((first, second) => clean(first.date || first.startDate).localeCompare(clean(second.date || second.startDate))),
    [storedEvents],
  );
  const surveys = useMemo(
    () => mergeSeed(NOVEMBER_CAREERS_SURVEY, storedSurveys, "survey")
      .sort((first, second) => clean(first.surveyName || first.title).localeCompare(clean(second.surveyName || second.title))),
    [storedSurveys],
  );
  const metrics = useMemo(
    () => getHousingDevelopmentMetrics(events, surveys, responses),
    [events, surveys, responses],
  );
  const partners = useMemo(() => listPartnerNames(events, surveys), [events, surveys]);
  const filteredResponses = useMemo(
    () => filterHousingResponses(responses, filters, surveys, events),
    [responses, filters, surveys, events],
  );
  const sources = useMemo(() => [...new Set([
    ...(HOUSING_DEVELOPMENT_SOURCE_VALUES || []),
    ...responses.map((row) => row.submissionSource || row.source).filter(Boolean),
  ])].sort(), [responses]);
  const responseCategories = useMemo(() => [...new Set(responses
    .map((row) => clean(row.responseCategory || row.category))
    .filter(Boolean))].sort(), [responses]);
  const reportSurvey = surveys.find((survey) => (
    recordId(survey, "survey") === reportFilters.surveyId
  )) || surveys[0];
  const reportFilteredResponses = useMemo(
    () => filterHousingResponses(responses, {
      ...reportFilters,
      surveyId: recordId(reportSurvey, "survey"),
    }, surveys, events),
    [responses, reportFilters, reportSurvey, surveys, events],
  );
  const report = useMemo(
    () => buildHousingSurveyReport(reportSurvey || {}, reportFilteredResponses),
    [reportSurvey, reportFilteredResponses],
  );
  const reportEvent = events.find((event) => recordId(event, "event") === (
    reportFilters.eventId || reportSurvey?.eventId
  ));
  const editingSurvey = surveys.find((survey) => recordId(survey, "survey") === editingSurveyId);
  const editingSurveyHasResponses = Boolean(editingSurveyId)
    && responses.some((response) => clean(response.surveyId) === editingSurveyId);
  const surveySlugLocked = Boolean(editingSurveyId)
    && isHousingSurveySlugLocked({ ...editingSurvey, hasResponses: editingSurveyHasResponses });

  useEffect(() => {
    if (surveys.length && !surveys.some((survey) => (
      recordId(survey, "survey") === reportFilters.surveyId
    ))) {
      setReportFilters((current) => ({
        ...current,
        surveyId: recordId(surveys[0], "survey"),
      }));
    }
  }, [surveys, reportFilters.surveyId]);

  const updateEvent = (event) => {
    const { name, value, checked, type } = event.target;
    setEventForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setEventErrors((current) => ({ ...current, [name]: undefined }));
  };

  const toggleEventSurvey = (surveyId) => {
    setEventForm((current) => ({
      ...current,
      surveyIds: current.surveyIds.includes(surveyId)
        ? current.surveyIds.filter((id) => id !== surveyId)
        : [...current.surveyIds, surveyId],
    }));
  };

  const submitEvent = async (event) => {
    event.preventDefault();
    const eventValues = {
      ...eventForm,
      surveySlugs: eventForm.surveyIds.map((surveyId) => {
        const survey = surveys.find((item) => recordId(item, "survey") === surveyId);
        return survey?.surveySlug || survey?.slug || surveyId;
      }),
    };
    const nextErrors = validateHousingEvent({ ...eventValues, flyerFile: eventFlyer });
    setEventErrors(nextErrors);
    setError("");
    setNotice("");
    if (Object.keys(nextErrors).length) return;
    setSavingEvent(true);
    try {
      const existing = events.find((item) => recordId(item, "event") === editingEventId) || {};
      let saved = editingEventId
        ? await updateHousingDevelopmentEvent(editingEventId, eventValues, existing, surveys)
        : await createHousingDevelopmentEvent(eventValues, eventFlyer, surveys);
      if (editingEventId && eventFlyer) {
        saved = await replaceHousingDevelopmentEventFlyer({ ...existing, ...saved, id: editingEventId }, eventFlyer);
      }
      setStoredEvents((current) => [...current.filter((item) => recordId(item, "event") !== recordId(saved, "event")), saved]);
      setEventForm(EMPTY_EVENT);
      setEventFlyer(null);
      setEditingEventId("");
      setNotice(`${saved.title} was ${editingEventId ? "updated" : `created as ${saved.status}`}.`);
    } catch (creationError) {
      if (creationError.validationErrors) setEventErrors(creationError.validationErrors);
      setError(creationError.message || "The event could not be created. Try again.");
    } finally {
      setSavingEvent(false);
    }
  };

  const editEvent = (item) => {
    setEditingEventId(recordId(item, "event"));
    setEventForm(eventToForm(item));
    setEventFlyer(null);
    setEventErrors({});
    setTab("Events");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetEventForm = () => {
    setEditingEventId("");
    setEventForm(EMPTY_EVENT);
    setEventFlyer(null);
    setEventErrors({});
  };

  const selectEventFlyer = (file) => {
    const validation = validateEventFlyer(file);
    setEventFlyer(validation.valid ? file : null);
    setEventErrors((current) => ({ ...current, flyerFile: validation.error || undefined }));
  };

  const replaceEventFlyer = async (item) => {
    const id = recordId(item, "event");
    const file = replacementFlyers[id];
    const validation = validateEventFlyer(file);
    if (!file || !validation.valid) {
      setError(validation.error || "Choose a flyer to upload.");
      return;
    }
    setReplacingFlyerId(id);
    setError("");
    setNotice("");
    try {
      const saved = await replaceHousingDevelopmentEventFlyer(item, file);
      setStoredEvents((current) => [...current.filter((event) => recordId(event, "event") !== id), saved]);
      setReplacementFlyers((current) => ({ ...current, [id]: null }));
      setNotice(`${item.title || item.eventName} flyer was updated.`);
    } catch (uploadError) {
      setError(uploadError.message || "The flyer could not be uploaded. Try again.");
    } finally {
      setReplacingFlyerId("");
    }
  };

  const updateSurvey = (event) => {
    const { name, value, checked, type } = event.target;
    setSurveyForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setSurveyErrors((current) => ({ ...current, [name]: undefined }));
  };

  const updateQuestion = (index, changes) => {
    const safeChanges = changes.type && isPersonalQuestionType(changes.type)
      ? { ...changes, personalData: true }
      : changes;
    setSurveyForm((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) => (
        questionIndex === index ? { ...question, ...safeChanges } : question
      )),
    }));
    setSurveyErrors((current) => ({
      ...current,
      questions: undefined,
      [`question-${index}-label`]: undefined,
      [`question-${index}-type`]: undefined,
      [`question-${index}-options`]: undefined,
    }));
  };

  const moveQuestion = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= surveyForm.questions.length) return;
    setSurveyForm((current) => {
      const questions = [...current.questions];
      [questions[index], questions[target]] = [questions[target], questions[index]];
      return { ...current, questions };
    });
  };

  const editSurvey = (survey) => {
    setEditingSurveyId(recordId(survey, "survey"));
    setSurveyForm(surveyToForm(survey));
    setSurveyErrors({});
    setTab("Surveys");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetSurveyForm = () => {
    setEditingSurveyId("");
    setSurveyForm(EMPTY_SURVEY);
    setSurveyErrors({});
  };

  const submitSurvey = async (event) => {
    event.preventDefault();
    const nextErrors = validateHousingSurvey(surveyForm);
    setSurveyErrors(nextErrors);
    setError("");
    setNotice("");
    if (Object.keys(nextErrors).length) return;
    setSavingSurvey(true);
    try {
      const existing = surveys.find((survey) => recordId(survey, "survey") === editingSurveyId) || {};
      const saved = editingSurveyId
        ? await updateHousingDevelopmentSurvey(editingSurveyId, surveyForm, {
          ...existing,
          hasResponses: responses.some((response) => clean(response.surveyId) === editingSurveyId),
        })
        : await createHousingDevelopmentSurvey(surveyForm);
      setStoredSurveys((current) => [...current.filter((item) => recordId(item, "survey") !== recordId(saved, "survey")), saved]);
      setNotice(`${saved.surveyName} was ${editingSurveyId ? "updated" : "created as a draft"}.`);
      resetSurveyForm();
    } catch (saveError) {
      if (saveError.validationErrors) setSurveyErrors(saveError.validationErrors);
      setError(saveError.message || "The survey could not be saved. Try again.");
    } finally {
      setSavingSurvey(false);
    }
  };

  const changeSurveyStatus = async (survey, status) => {
    const id = recordId(survey, "survey");
    setBusySurveyId(id);
    setError("");
    setNotice("");
    try {
      await setHousingSurveyStatus(id, status);
      setStoredSurveys((current) => current.map((item) => recordId(item, "survey") === id
        ? { ...item, status, published: status === "published", active: status === "published" }
        : item));
      setNotice(`${survey.surveyName || survey.title} is now ${status}.`);
    } catch {
      setError("The survey status could not be changed. Try again.");
    } finally {
      setBusySurveyId("");
    }
  };

  const duplicateSurvey = async (survey) => {
    const id = recordId(survey, "survey");
    setBusySurveyId(id);
    setError("");
    setNotice("");
    try {
      const copy = await duplicateHousingDevelopmentSurvey(survey);
      setStoredSurveys((current) => [...current, copy]);
      setNotice(`${copy.surveyName} was created as a draft.`);
      editSurvey(copy);
    } catch {
      setError("The survey could not be duplicated. Try again.");
    } finally {
      setBusySurveyId("");
    }
  };

  const copySurveyLink = async (survey) => {
    const url = buildHousingSurveyAbsoluteUrl(survey.surveySlug || survey.slug);
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Survey link copied.");
    } catch {
      setNotice(`Copy unavailable. Select this link manually: ${url}`);
    }
  };

  const updateFilter = (event) => {
    setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const updateReportFilter = (event) => {
    setReportFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const exportResponses = (survey = null, filteredRows = null) => {
    const targetSurvey = survey || selectHousingSurveyForCsv(surveys, filters.surveyId);
    if (!targetSurvey) {
      setError("Select one survey before exporting so its answer columns can be included.");
      return;
    }
    setError("");
    const targetEventId = survey
      ? reportFilters.eventId || targetSurvey.eventId
      : targetSurvey.eventId || filters.eventId;
    const targetEvent = events.find((item) => recordId(item, "event") === targetEventId) || {};
    const rows = Array.isArray(filteredRows)
      ? filteredRows
      : survey
        ? responses.filter((response) => response.surveyId === recordId(survey, "survey"))
        : filteredResponses;
    downloadHousingSurveyCsv(
      housingSurveyResponsesToCsv(rows, targetSurvey, targetEvent, { includePersonalData }),
      housingSurveyCsvFilename(targetSurvey, targetEvent),
    );
  };

  const renderOverview = () => (
    <div className="grid gap-6">
      <section aria-labelledby="housing-overview-title">
        <h2 id="housing-overview-title" className="text-2xl font-black">Programme overview</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <MetricCard label="Upcoming events" value={metrics.upcomingEvents} icon={CalendarDays} />
          <MetricCard label="Total events" value={metrics.totalEvents} icon={CalendarDays} />
          <MetricCard label="Active surveys" value={metrics.activeSurveys} icon={FileText} />
          <MetricCard label="Survey responses" value={metrics.totalSurveyResponses} />
          <MetricCard label="Event attendance" value={metrics.eventAttendance} icon={Users} />
          <MetricCard label="Partner organisations" value={metrics.partnerOrganisations} icon={Users} />
        </div>
      </section>
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <h2 className="text-xl font-black">Next events</h2>
          <div className="mt-4 grid gap-3">{events.slice(0, 4).map((item) => (
            <div key={recordId(item, "event")} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-start justify-between gap-3"><strong>{item.title || item.eventName}</strong><Status>{item.status}</Status></div>
              <p className="mt-2 text-sm text-white/65">{item.date || item.startDate} · {item.venueName || item.location}</p>
            </div>
          ))}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <h2 className="text-xl font-black">Current surveys</h2>
          <div className="mt-4 grid gap-3">{surveys.slice(0, 4).map((survey) => (
            <div key={recordId(survey, "survey")} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="flex items-start justify-between gap-3"><strong>{survey.surveyName || survey.title}</strong><Status>{survey.status}</Status></div>
              <p className="mt-2 text-sm text-white/65">{responses.filter((row) => row.surveyId === recordId(survey, "survey")).length} response(s)</p>
            </div>
          ))}</div>
        </div>
      </section>
    </div>
  );

  const renderEvents = () => (
    <div className="grid gap-7 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.75fr)]">
      <section aria-labelledby="housing-events-title">
        <h2 id="housing-events-title" className="text-2xl font-black">Housing &amp; Development events</h2>
        <p className="mt-2 text-white/65">Events remain drafts until an authorised administrator approves them.</p>
        <div className="mt-5 grid gap-4">{events.map((item) => {
          const eventId = recordId(item, "event");
          const publishedSurveys = getPublishedSurveysForEvent(item, surveys);
          return (
            <article key={eventId} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="text-xl font-black">{item.title || item.eventName}</h3><p className="mt-1 text-white/65">{item.date || item.startDate} · {item.startTime}–{item.endTime}</p></div><Status>{item.status}</Status></div>
              <p className="mt-3">{item.venueName || item.location}</p><p className="text-sm text-white/65">{item.address}</p>
              <p className="mt-3 text-sm text-white/75">Partners: {recordPartners(item).join(", ") || "None recorded"}</p>
              <p className="mt-1 text-sm text-white/60">Registration {item.registrationEnabled || item.registrationOpen ? "enabled" : "not enabled"} · Flyer {item.flyerStoragePath || item.flyerUrl ? "available" : "coming soon"}</p>
              <button type="button" className="mt-3 rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => editEvent(item)}>Edit event details</button>
              <div className="mt-4 grid gap-3 sm:grid-cols-[140px_minmax(0,1fr)]">
                <EventFlyer event={item} compact showFallback={false} eager />
                <div className="grid content-start gap-2">
                  <label className="grid gap-1 text-sm font-bold">
                    {item.flyerStoragePath || item.flyerUrl ? "Replace flyer" : "Upload flyer"}
                    <input
                      className={inputClass()}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      disabled={item.status !== "approved" || replacingFlyerId === eventId}
                      onChange={(event) => setReplacementFlyers((current) => ({
                        ...current,
                        [eventId]: event.target.files?.[0] || null,
                      }))}
                    />
                  </label>
                  <button
                    type="button"
                    className="min-h-11 rounded-xl bg-white/10 px-3 py-2 font-bold disabled:opacity-40"
                    disabled={!replacementFlyers[eventId] || item.status !== "approved" || replacingFlyerId === eventId}
                    onClick={() => replaceEventFlyer(item)}
                  >
                    {replacingFlyerId === eventId ? "Uploading…" : "Save flyer"}
                  </button>
                  {item.status !== "approved" ? <small className="text-white/55">Approve this event before uploading its public flyer.</small> : null}
                </div>
              </div>
              {publishedSurveys.length ? (
                <section className="mt-5 border-t border-white/10 pt-5" aria-label={`Published surveys for ${item.title || item.eventName}`}>
                  <h4 className="font-black">Published surveys</h4>
                  <div className="mt-3 grid gap-4">{publishedSurveys.map((survey) => {
                    const surveyId = recordId(survey, "survey");
                    const slug = survey.surveySlug || survey.slug;
                    const publicUrl = buildHousingSurveyAbsoluteUrl(slug);
                    const trackedQrUrl = buildHousingSurveyAbsoluteUrl(slug, "event-qr");
                    return (
                      <div key={surveyId} className="rounded-xl border border-white/10 bg-black/20 p-4">
                        <h5 className="font-bold">{survey.surveyName || survey.title}</h5>
                        <p className="mt-1 break-all text-sm text-cyan-200">{publicUrl}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => copySurveyLink(survey)}><ClipboardCopy className="inline h-4 w-4" /> Copy link</button>
                          <a className="rounded-xl bg-white/10 px-3 py-2 font-bold" href={publicUrl} target="_blank" rel="noreferrer"><ExternalLink className="inline h-4 w-4" /> Open survey</a>
                        </div>
                        <div className="mt-4"><HousingDevelopmentQr url={trackedQrUrl} filename={`${slug}-event-qr`} title={`${survey.surveyName || survey.title} survey QR code`} /></div>
                      </div>
                    );
                  })}</div>
                </section>
              ) : null}
            </article>
          );
        })}</div>
      </section>
      <section className="rounded-2xl border border-cyan-300/25 bg-cyan-400/5 p-5" aria-labelledby="create-housing-event-title">
        <div className="flex flex-wrap items-start justify-between gap-3"><h2 id="create-housing-event-title" className="text-2xl font-black">{editingEventId ? "Edit event" : "Create future event"}</h2>{editingEventId ? <button type="button" className="rounded-xl border border-white/20 px-3 py-2 font-bold" onClick={resetEventForm}><X className="inline h-4 w-4" /> Cancel editing</button> : null}</div>
        <form className="mt-5 grid gap-4" onSubmit={submitEvent} noValidate>
          <Field label="Title *" name="title" error={eventErrors.title}><input className={inputClass()} name="title" value={eventForm.title} onChange={updateEvent} aria-invalid={Boolean(eventErrors.title)} /></Field>
          <div className="grid gap-4 sm:grid-cols-2"><Field label="Date *" name="date" error={eventErrors.date}><input className={inputClass()} type="date" name="date" value={eventForm.date} onChange={updateEvent} /></Field><Field label="Status" name="status"><select className={inputClass()} name="status" value={eventForm.status} onChange={updateEvent}><option value="draft">Draft</option><option value="approved">Approved (public)</option><option value="cancelled">Cancelled</option></select><small className="text-white/55">Keep past public events approved; their date moves them into Previous Events automatically.</small></Field></div>
          <div className="grid gap-4 sm:grid-cols-2"><Field label="Start time *" name="startTime" error={eventErrors.startTime}><input className={inputClass()} type="time" name="startTime" value={eventForm.startTime} onChange={updateEvent} /></Field><Field label="End time *" name="endTime" error={eventErrors.endTime}><input className={inputClass()} type="time" name="endTime" value={eventForm.endTime} onChange={updateEvent} /></Field></div>
          <Field label="Venue *" name="venueName" error={eventErrors.venueName}><input className={inputClass()} name="venueName" value={eventForm.venueName} onChange={updateEvent} /></Field>
          <Field label="Address *" name="address" error={eventErrors.address}><textarea className={`${inputClass()} p-3`} rows="3" name="address" value={eventForm.address} onChange={updateEvent} /></Field>
          <Field label="Description *" name="description" error={eventErrors.description}><textarea className={`${inputClass()} p-3`} rows="5" maxLength="4000" name="description" value={eventForm.description} onChange={updateEvent} /></Field>
          <Field label="Partner organisations *" name="partnerOrganisations" error={eventErrors.partnerOrganisations}><input className={inputClass()} name="partnerOrganisations" value={eventForm.partnerOrganisations} onChange={updateEvent} /><small className="text-white/55">Separate organisations with commas.</small></Field>
          <div className="grid gap-4 sm:grid-cols-2"><Field label="Registration total" name="registrationCount" error={eventErrors.registrationCount}><input className={inputClass()} type="number" min="0" step="1" name="registrationCount" value={eventForm.registrationCount} onChange={updateEvent} /><small className="text-white/55">Optional total from an external registration system.</small></Field><Field label="Attendance total" name="attendanceCount" error={eventErrors.attendanceCount}><input className={inputClass()} type="number" min="0" step="1" name="attendanceCount" value={eventForm.attendanceCount} onChange={updateEvent} /><small className="text-white/55">Optional checked-in or confirmed attendance total.</small></Field></div>
          <Field label={editingEventId ? "Replace event flyer" : "Event flyer"} name="flyerFile" error={eventErrors.flyerFile}><input className={inputClass()} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(event) => selectEventFlyer(event.target.files?.[0] || null)} /><small className="text-white/55">Optional JPG, PNG, WEBP or PDF under 5 MB. Select Approved status before uploading.</small></Field>
          <fieldset className="grid gap-2 rounded-xl border border-white/15 bg-black/20 p-4"><legend className="px-1 font-bold">Associated surveys</legend><p className="text-sm text-white/55">Optional. Select any surveys that belong to this event.</p>{surveys.map((survey) => { const id = recordId(survey, "survey"); return <label key={id} className="flex gap-2"><input type="checkbox" checked={eventForm.surveyIds.includes(id)} onChange={() => toggleEventSurvey(id)} /> {survey.surveyName || survey.title}</label>; })}{!surveys.length ? <p className="text-sm text-white/55">Create a survey first, or add one later.</p> : null}</fieldset>
          <label className="flex gap-3 rounded-xl border border-white/15 bg-black/20 p-4"><input type="checkbox" name="registrationEnabled" checked={eventForm.registrationEnabled} onChange={updateEvent} /><span><strong>Registration enabled</strong><small className="block text-white/55">Only enable this when the event needs attendee registration.</small></span></label>
          {eventForm.registrationEnabled ? <Field label="Registration URL *" name="registrationUrl" error={eventErrors.registrationUrl}><input className={inputClass()} type="url" name="registrationUrl" value={eventForm.registrationUrl} onChange={updateEvent} placeholder="https://…" /><small className="text-white/55">Required when registration is enabled. Use the complete secure registration page URL.</small></Field> : null}
          <button className="min-h-12 rounded-xl bg-cyan-700 px-4 font-black hover:bg-cyan-600 disabled:opacity-50" disabled={savingEvent}>{savingEvent ? "Saving event…" : editingEventId ? "Save event changes" : "Create event"}</button>
        </form>
      </section>
    </div>
  );

  const renderSurveyBuilder = () => (
    <section className="rounded-2xl border border-cyan-300/25 bg-cyan-400/5 p-5" aria-labelledby="survey-builder-title">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 id="survey-builder-title" className="text-2xl font-black">{editingSurveyId ? "Edit survey" : "Create survey"}</h2><p className="mt-1 text-white/65">Survey questions are stored as reusable configuration, not hard-coded pages.</p></div>{editingSurveyId ? <button type="button" className="rounded-xl border border-white/20 px-3 py-2 font-bold" onClick={resetSurveyForm}><X className="inline h-4 w-4" /> Cancel editing</button> : null}</div>
      <form className="mt-5 grid gap-4" onSubmit={submitSurvey} noValidate>
        <div className="grid gap-4 md:grid-cols-2"><Field label="Survey name *" name="surveyName" error={surveyErrors.surveyName}><input className={inputClass()} name="surveyName" value={surveyForm.surveyName} onChange={updateSurvey} /></Field><Field label="Permanent URL slug *" name="surveySlug" error={surveyErrors.surveySlug}><input className={inputClass()} name="surveySlug" value={surveyForm.surveySlug} onChange={updateSurvey} placeholder="resident-feedback-2027" disabled={surveySlugLocked} />{surveySlugLocked ? <small className="text-white/55">The public URL is locked because this survey has been published or has responses.</small> : null}</Field></div>
        <Field label="Description *" name="description" error={surveyErrors.description}><textarea className={`${inputClass()} p-3`} rows="4" maxLength="3000" name="description" value={surveyForm.description} onChange={updateSurvey} /></Field>
        <div className="grid gap-4 md:grid-cols-2"><Field label="Associated event" name="eventId"><select className={inputClass()} name="eventId" value={surveyForm.eventId} onChange={updateSurvey}><option value="">No event</option>{events.map((item) => <option key={recordId(item, "event")} value={recordId(item, "event")}>{item.title || item.eventName}</option>)}</select></Field><Field label="Partner organisations" name="partnerOrganisations"><input className={inputClass()} name="partnerOrganisations" value={surveyForm.partnerOrganisations} onChange={updateSurvey} /></Field></div>
        <div className="grid gap-4 md:grid-cols-2"><Field label="Open date" name="openDate" error={surveyErrors.openDate}><input className={inputClass()} type="date" name="openDate" value={surveyForm.openDate} onChange={updateSurvey} /></Field><Field label="Close date" name="closeDate" error={surveyErrors.closeDate}><input className={inputClass()} type="date" name="closeDate" value={surveyForm.closeDate} onChange={updateSurvey} /></Field></div>
        <label className="flex gap-3 rounded-xl border border-white/15 bg-black/20 p-4"><input type="checkbox" name="publicVisibility" checked={surveyForm.publicVisibility} onChange={updateSurvey} /><span><strong>Show in Current Surveys</strong><small className="block text-white/55">The survey must also be published before public visitors can open it.</small></span></label>
        <div className="flex flex-wrap items-center justify-between gap-3"><div><h3 className="text-xl font-black">Questions</h3>{surveyErrors.questions ? <p className="text-sm text-amber-200">{surveyErrors.questions}</p> : null}<p className="text-sm text-white/55">{surveyForm.questions.length} of 100 questions</p></div><button type="button" className="rounded-xl bg-white/10 px-4 py-2 font-bold hover:bg-white/15 disabled:opacity-40" disabled={surveyForm.questions.length >= 100} onClick={() => setSurveyForm((current) => ({ ...current, questions: [...current.questions, newQuestion(current.questions.length)] }))}><Plus className="inline h-4 w-4" /> Add question</button></div>
        <div className="grid gap-4">{surveyForm.questions.map((question, index) => (
          <div className="hd-admin-question grid gap-4" key={`${question.id}-${index}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><strong>Question {index + 1}</strong><div className="flex gap-2"><button type="button" className="rounded-lg border border-white/15 px-2 py-1 disabled:opacity-40" disabled={index === 0} onClick={() => moveQuestion(index, -1)}>Move up</button><button type="button" className="rounded-lg border border-white/15 px-2 py-1 disabled:opacity-40" disabled={index === surveyForm.questions.length - 1} onClick={() => moveQuestion(index, 1)}>Move down</button><button type="button" className="rounded-lg border border-red-300/30 px-2 py-1 text-red-100" onClick={() => setSurveyForm((current) => ({ ...current, questions: current.questions.filter((_, questionIndex) => questionIndex !== index) }))}>Remove</button></div></div>
            <div className="grid gap-4 md:grid-cols-2"><Field label="Question wording *" name={`question-${index}-label`} error={surveyErrors[`question-${index}-label`]} className="md:col-span-2"><input className={inputClass()} value={question.label} onChange={(event) => updateQuestion(index, { label: event.target.value })} /></Field><Field label="Field ID" name={`question-${index}-id`} error={surveyErrors[`question-${index}-id`]}><input className={inputClass()} value={question.id} onChange={(event) => updateQuestion(index, { id: event.target.value })} /></Field><Field label="Question type" name={`question-${index}-type`} error={surveyErrors[`question-${index}-type`]}><select className={inputClass()} value={question.type} onChange={(event) => updateQuestion(index, { type: event.target.value, options: needsOptions(event.target.value) ? question.options : [], ...(isCustomRating(event.target.value) ? { ratingMin: Number(question.ratingMin || question.min || 1), ratingMax: Number(question.ratingMax || question.max || 5) } : {}) })}>{questionTypeChoices().map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></Field></div>
            <Field label="Help text" name={`question-${index}-help`}><input className={inputClass()} value={question.helpText || ""} onChange={(event) => updateQuestion(index, { helpText: event.target.value })} /></Field>
            {isCustomRating(question.type) ? <div className="grid gap-4 sm:grid-cols-2"><Field label="Minimum (1–10) *" name={`question-${index}-ratingMin`} error={surveyErrors[`question-${index}-ratingMin`]}><input className={inputClass()} type="number" min="1" max="10" step="1" value={question.ratingMin ?? question.min ?? 1} onChange={(event) => updateQuestion(index, { ratingMin: Number(event.target.value), min: Number(event.target.value) })} /></Field><Field label="Maximum (1–10) *" name={`question-${index}-ratingMax`} error={surveyErrors[`question-${index}-ratingMax`]}><input className={inputClass()} type="number" min="1" max="10" step="1" value={question.ratingMax ?? question.max ?? 5} onChange={(event) => updateQuestion(index, { ratingMax: Number(event.target.value), max: Number(event.target.value) })} /></Field></div> : null}
            {needsOptions(question.type) ? <Field label="Answer options *" name={`question-${index}-options`} error={surveyErrors[`question-${index}-options`]}><textarea className={`${inputClass()} p-3`} rows="5" value={(question.options || []).join("\n")} onChange={(event) => updateQuestion(index, { options: event.target.value.split("\n").map(clean).filter(Boolean) })} /><small className="text-white/55">One option per line, in display order.</small></Field> : null}
            <div className="flex flex-wrap gap-5"><label className="flex items-center gap-2"><input type="checkbox" checked={question.required === true} onChange={(event) => updateQuestion(index, { required: event.target.checked })} /> Required</label><label className="flex items-center gap-2"><input type="checkbox" checked={question.personalData === true || isPersonalQuestionType(question.type)} disabled={isPersonalQuestionType(question.type)} onChange={(event) => updateQuestion(index, { personalData: event.target.checked })} /> Contains personal data{isPersonalQuestionType(question.type) ? " (required for email/phone)" : ""}</label></div>
          </div>
        ))}</div>
        <button className="min-h-12 rounded-xl bg-cyan-700 px-4 font-black hover:bg-cyan-600 disabled:opacity-50" disabled={savingSurvey}>{savingSurvey ? "Saving survey…" : editingSurveyId ? "Save survey changes" : "Create draft survey"}</button>
      </form>
    </section>
  );

  const renderSurveys = () => (
    <div className="grid gap-7 xl:grid-cols-[minmax(0,1.1fr)_minmax(380px,0.9fr)]">
      <section aria-labelledby="housing-surveys-title"><h2 id="housing-surveys-title" className="text-2xl font-black">Surveys</h2><p className="mt-2 text-white/65">Create, preview, duplicate and control the public lifecycle of reusable surveys.</p><div className="mt-5 grid gap-4">{surveys.map((survey) => {
        const id = recordId(survey, "survey");
        const slug = survey.surveySlug || survey.slug;
        const publicUrl = buildHousingSurveyAbsoluteUrl(slug);
        const trackedQrUrl = buildHousingSurveyAbsoluteUrl(slug, "event-qr");
        return <article key={id} className="rounded-2xl border border-white/10 bg-white/5 p-5"><div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="text-xl font-black">{survey.surveyName || survey.title}</h3><p className="mt-1 break-all text-sm text-cyan-200">{publicUrl}</p></div><Status>{survey.status}</Status></div><p className="mt-3 text-white/70">{survey.description}</p><p className="mt-2 text-sm text-white/60">{(survey.questions || []).length} question(s) · {responses.filter((row) => row.surveyId === id).length} response(s) · {recordPartners(survey).join(", ")}</p><div className="mt-4 flex flex-wrap gap-2"><button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => editSurvey(survey)}>Edit</button><button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" disabled={busySurveyId === id} onClick={() => duplicateSurvey(survey)}><Copy className="inline h-4 w-4" /> Duplicate</button><button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => copySurveyLink(survey)}><ClipboardCopy className="inline h-4 w-4" /> Copy link</button><button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => setPreviewSurvey(survey)}><ExternalLink className="inline h-4 w-4" /> Preview</button><button type="button" className="rounded-xl bg-white/10 px-3 py-2 font-bold" onClick={() => setQrSurveyId((current) => current === id ? "" : id)}>QR</button>{survey.status !== "published" ? <button type="button" className="rounded-xl bg-emerald-700 px-3 py-2 font-bold" disabled={busySurveyId === id} onClick={() => changeSurveyStatus(survey, "published")}>Publish</button> : <button type="button" className="rounded-xl bg-amber-700 px-3 py-2 font-bold" disabled={busySurveyId === id} onClick={() => changeSurveyStatus(survey, "draft")}>Unpublish</button>}<button type="button" className="rounded-xl border border-red-300/30 px-3 py-2 font-bold text-red-100" disabled={busySurveyId === id || survey.status === "closed"} onClick={() => changeSurveyStatus(survey, "closed")}>Close</button></div>{qrSurveyId === id ? <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4"><HousingDevelopmentQr url={trackedQrUrl} filename={`${slug}-event-qr`} title={`${survey.surveyName || survey.title} survey QR code`} /></div> : null}</article>;
      })}</div></section>
      {renderSurveyBuilder()}
    </div>
  );

  const renderResponseFilters = () => (
    <div className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Survey response filters">
      <label className="grid gap-1"><span className="text-sm font-bold">Search answers</span><div className="relative"><Search className="absolute left-3 top-3 h-5 w-5 text-white/45" aria-hidden="true" /><input className={`${inputClass()} w-full pl-10`} name="search" value={filters.search} onChange={updateFilter} /></div></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Event</span><select className={inputClass()} name="eventId" value={filters.eventId} onChange={updateFilter}><option value="">All events</option>{events.map((item) => <option key={recordId(item, "event")} value={recordId(item, "event")}>{item.title || item.eventName}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Survey</span><select className={inputClass()} name="surveyId" value={filters.surveyId} onChange={updateFilter}><option value="">All surveys</option>{surveys.map((survey) => <option key={recordId(survey, "survey")} value={recordId(survey, "survey")}>{survey.surveyName || survey.title}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Partner</span><select className={inputClass()} name="partner" value={filters.partner} onChange={updateFilter}><option value="">All partners</option>{partners.map((partner) => <option key={partner}>{partner}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Source</span><select className={inputClass()} name="source" value={filters.source} onChange={updateFilter}><option value="">All sources</option>{sources.map((source) => <option key={source}>{source}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Response category</span><select className={inputClass()} name="responseCategory" value={filters.responseCategory} onChange={updateFilter}><option value="">All categories</option>{responseCategories.map((category) => <option key={category}>{category}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">From date</span><input className={inputClass()} type="date" name="dateFrom" value={filters.dateFrom} onChange={updateFilter} /></label>
      <label className="grid gap-1"><span className="text-sm font-bold">To date</span><input className={inputClass()} type="date" name="dateTo" value={filters.dateTo} onChange={updateFilter} /></label>
      <button type="button" className="min-h-12 self-end rounded-xl border border-white/20 bg-white/10 px-4 font-bold" onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</button>
    </div>
  );

  const renderReportFilters = () => (
    <div className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Report filters">
      <label className="grid gap-1"><span className="text-sm font-bold">Survey</span><select className={inputClass()} name="surveyId" value={recordId(reportSurvey, "survey")} onChange={updateReportFilter}>{surveys.map((survey) => <option key={recordId(survey, "survey")} value={recordId(survey, "survey")}>{survey.surveyName || survey.title}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Event</span><select className={inputClass()} name="eventId" value={reportFilters.eventId} onChange={updateReportFilter}><option value="">All events</option>{events.map((item) => <option key={recordId(item, "event")} value={recordId(item, "event")}>{item.title || item.eventName}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Partner</span><select className={inputClass()} name="partner" value={reportFilters.partner} onChange={updateReportFilter}><option value="">All partners</option>{partners.map((partner) => <option key={partner}>{partner}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Source</span><select className={inputClass()} name="source" value={reportFilters.source} onChange={updateReportFilter}><option value="">All sources</option>{sources.map((source) => <option key={source}>{source}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">Response category</span><select className={inputClass()} name="responseCategory" value={reportFilters.responseCategory} onChange={updateReportFilter}><option value="">All categories</option>{responseCategories.map((category) => <option key={category}>{category}</option>)}</select></label>
      <label className="grid gap-1"><span className="text-sm font-bold">From date</span><input className={inputClass()} type="date" name="dateFrom" value={reportFilters.dateFrom} onChange={updateReportFilter} /></label>
      <label className="grid gap-1"><span className="text-sm font-bold">To date</span><input className={inputClass()} type="date" name="dateTo" value={reportFilters.dateTo} onChange={updateReportFilter} /></label>
      <button type="button" className="min-h-12 self-end rounded-xl border border-white/20 bg-white/10 px-4 font-bold" onClick={() => setReportFilters({ ...EMPTY_FILTERS, surveyId: recordId(reportSurvey, "survey") })}>Clear report filters</button>
    </div>
  );

  const renderResponses = () => (
    <div className="grid gap-5"><div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-2xl font-black">Survey responses</h2><p className="mt-1 text-white/65">Personal answers are not displayed in this list. Use authorised CSV export only when necessary.</p></div><div className="flex flex-wrap items-center gap-3"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includePersonalData} onChange={(event) => setIncludePersonalData(event.target.checked)} /> Include personal data in authorised CSV</label><button type="button" className="rounded-xl bg-cyan-700 px-4 py-3 font-black disabled:cursor-not-allowed disabled:opacity-40" disabled={!filters.surveyId} title={!filters.surveyId ? "Select one survey before exporting" : "Export the selected survey responses"} onClick={() => exportResponses()}><Download className="inline h-4 w-4" /> Export CSV</button></div></div>{renderResponseFilters()}{!filters.surveyId ? <p className="text-sm text-amber-100">Select one survey to enable CSV export and include its answer columns.</p> : null}<p aria-live="polite">Showing {filteredResponses.length} of {responses.length} responses.</p><div className="hd-admin-table-wrap"><table className="hd-admin-table"><thead><tr><th>Response</th><th>Submitted</th><th>Survey</th><th>Event</th><th>Source</th></tr></thead><tbody>{filteredResponses.map((response) => { const survey = surveys.find((item) => recordId(item, "survey") === response.surveyId); const event = events.find((item) => recordId(item, "event") === response.eventId); return <tr key={response.id || response.responseId}><td className="font-mono text-xs">{response.responseId || response.id}</td><td>{formatAdminDate(response.submittedAt || response.createdAt)}</td><td>{survey?.surveyName || survey?.title || response.surveyId}</td><td>{event?.title || event?.eventName || response.eventId || "—"}</td><td>{response.submissionSource || response.source || "unknown"}</td></tr>; })}{!filteredResponses.length ? <tr><td colSpan="5">No responses match these filters.</td></tr> : null}</tbody></table></div></div>
  );

  const renderPartners = () => (
    <div><h2 className="text-2xl font-black">Partner reporting</h2><p className="mt-2 text-white/65">Internal programme activity grouped by associated organisation.</p><div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">{partners.map((partner) => { const partnerReport = getPartnerReport(partner, events, surveys, responses); return <article key={partner} className="rounded-2xl border border-white/10 bg-white/5 p-5"><h3 className="text-xl font-black">{partner}</h3><dl className="mt-4 grid grid-cols-3 gap-3 text-center"><div><dt className="text-xs text-white/55">Events</dt><dd className="text-2xl font-black">{partnerReport.events.length}</dd></div><div><dt className="text-xs text-white/55">Surveys</dt><dd className="text-2xl font-black">{partnerReport.surveys.length}</dd></div><div><dt className="text-xs text-white/55">Responses</dt><dd className="text-2xl font-black">{partnerReport.responseCount}</dd></div></dl><div className="mt-4 text-sm text-white/70"><p>{partnerReport.events.map((item) => item.title || item.eventName).join(", ") || "No events"}</p></div></article>; })}{!partners.length ? <p>No partner organisations have been recorded.</p> : null}</div></div>
  );

  const renderReports = () => {
    const registrationCount = numericMetricValue(reportEvent, ["registrationCount", "registrations"]);
    const attendanceCount = numericMetricValue(reportEvent, ["attendanceCount", "attended"]);
    const noShowCount = registrationCount !== null && attendanceCount !== null
      ? Math.max(registrationCount - attendanceCount, 0)
      : null;
    const completionRate = registrationCount !== null && registrationCount > 0
      ? Math.round((report.totalResponses / registrationCount) * 100)
      : null;
    return (
      <div className="grid gap-6">
        <div>
          <h2 className="text-2xl font-black">Survey &amp; event report</h2>
          <p className="mt-1 text-white/65">Question-by-question evidence suitable for partner reporting.</p>
        </div>
        {renderReportFilters()}
        <p aria-live="polite">Report includes {report.totalResponses} filtered response(s).</p>
        <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-cyan-300">Event summary</p>
          <h3 className="mt-2 text-xl font-black">{reportEvent?.title || reportEvent?.eventName || "All associated events"}</h3>
          <p className="mt-2 text-white/70">{reportEvent?.date || reportEvent?.startDate || "Date not recorded"} · {reportEvent?.venueName || reportEvent?.location || "Location not recorded"}</p>
          <p className="mt-1 text-white/70">Partners: {recordPartners(reportSurvey).join(", ") || recordPartners(reportEvent).join(", ") || "None recorded"}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5"><MetricCard label="Registrations" value={registrationCount ?? "Not recorded"} icon={Users} /><MetricCard label="Attendance" value={attendanceCount ?? "Not recorded"} icon={Users} /><MetricCard label="No-shows" value={noShowCount ?? "Not recorded"} icon={Users} /><MetricCard label="Survey responses" value={report.totalResponses} /><MetricCard label="Completion rate" value={completionRate === null ? "Not available" : `${completionRate}%`} /></div>
        </section>
        <div className="flex flex-wrap items-center justify-between gap-3"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includePersonalData} onChange={(event) => setIncludePersonalData(event.target.checked)} /> Include personal data in authorised CSV</label><button type="button" className="rounded-xl bg-cyan-700 px-4 py-3 font-black" onClick={() => exportResponses(reportSurvey, reportFilteredResponses)}><Download className="inline h-4 w-4" /> Export filtered report</button></div>
        <div className="grid gap-4 lg:grid-cols-2"><AccessibleBarChart title="Responses by source" items={report.responsesBySource} /><AccessibleBarChart title="Responses by date" items={report.responsesByDate} /></div>
        <section>
          <h3 className="text-xl font-black">Survey results</h3>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">{report.questions.map((question) => <article key={question.id} className="rounded-2xl border border-white/10 bg-white/5 p-5"><div className="flex items-start justify-between gap-3"><h4 className="font-black">{question.label}</h4><span className="text-sm text-white/55">{question.responded} answered · {question.skipped} skipped</span></div>{question.kind === "rating" ? <><dl className="my-4 flex gap-6"><div><dt className="text-sm text-white/55">Average</dt><dd className="text-2xl font-black">{question.average ?? "—"}</dd></div><div><dt className="text-sm text-white/55">Median</dt><dd className="text-2xl font-black">{question.median ?? "—"}</dd></div></dl><AccessibleBarChart title="Rating distribution" items={question.distribution} /></> : null}{question.kind === "categorical" ? <div className="mt-4"><AccessibleBarChart title="Answer distribution" items={question.values} /></div> : null}{question.kind === "free-text" && question.personalData ? <p className="mt-4 text-sm text-white/65">Personal responses are excluded from aggregate reporting. Use an authorised CSV export only when necessary.</p> : null}{question.kind === "free-text" && !question.personalData ? <div className="mt-4"><label className="grid gap-1"><span className="text-sm font-bold">Search written responses</span><input className={inputClass()} value={freeTextSearch} onChange={(event) => setFreeTextSearch(event.target.value)} /></label><div className="mt-3 max-h-80 space-y-3 overflow-y-auto">{question.freeText.filter((item) => !freeTextSearch || item.value.toLowerCase().includes(freeTextSearch.toLowerCase())).map((item) => <blockquote key={`${question.id}-${item.responseId}`} className="rounded-xl border border-white/10 bg-black/20 p-3">{item.value}</blockquote>)}{!question.freeText.length ? <p>No written responses.</p> : null}</div></div> : null}</article>)}</div>
        </section>
      </div>
    );
  };

  const panels = {
    Overview: renderOverview,
    Events: renderEvents,
    Surveys: renderSurveys,
    Responses: renderResponses,
    Partners: renderPartners,
    Reports: renderReports,
  };

  return (
    <div className="hd-admin mx-auto max-w-[1500px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"><div><p className="text-sm font-bold uppercase tracking-[0.16em] text-cyan-300">Authorised staff</p><h1 className="mt-2 text-3xl font-black md:text-5xl">Housing &amp; Development</h1><p className="mt-3 max-w-3xl text-white/70">Manage housing events, reusable surveys, responses, partners and evidence reporting.</p></div><Link className="inline-flex min-h-11 items-center justify-center rounded-xl border border-white/20 bg-white/10 px-4 py-2 font-bold" to="/admin">Back to admin</Link></header>
      <div className="hd-admin-tablist mt-7" role="tablist" aria-label="Housing and Development administration">{TABS.map((item) => <button key={item} type="button" role="tab" className="hd-admin-tab" aria-selected={tab === item} onClick={() => setTab(item)}>{item}</button>)}</div>
      {notice ? <div className="mt-4 rounded-xl border border-emerald-300/25 bg-emerald-300/10 p-4" role="status">{notice}</div> : null}
      {error ? <div className="mt-4 rounded-xl border border-red-300/25 bg-red-300/10 p-4" role="alert">{error}</div> : null}
      <SurveyPreview survey={previewSurvey} onClose={() => setPreviewSurvey(null)} />
      <main className="mt-6" role="tabpanel">{loading ? <p>Loading Housing &amp; Development administration…</p> : panels[tab]()}</main>
    </div>
  );
}

